import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import SentryRouteSync from "./components/SentryRouteSync";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { UserPreferencesProvider } from "@/hooks/useUserPreferences";
import { GoogleCalendarProvider } from "@/hooks/useGoogleCalendar";
import { AppleCalendarProvider } from "@/hooks/useAppleCalendar";
import { KrogerProvider } from "@/hooks/useKroger";
import Index from "./pages/Index.tsx";
import KrogerCallback from "./pages/KrogerCallback.tsx";
import PinterestCallback from "./pages/PinterestCallback.tsx";
import GoogleCallback from "./pages/GoogleCallback.tsx";
import NotFound from "./pages/NotFound.tsx";
import SignIn from "./pages/SignIn.tsx";
import SignUp from "./pages/SignUp.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import AuthCallback from "./pages/AuthCallback.tsx";
import Settings from "./pages/Settings.tsx";
import Pro from "./pages/Pro.tsx";
import SplashLanding from "./pages/SplashLanding.tsx";
import OfflineScreen from "./components/OfflineScreen";
import AppStoreNudge from "./components/AppStoreNudge";
import ReviewPrompt from "./components/ReviewPrompt";
import EntitlementFlipListener from "./components/EntitlementFlipListener";
import InAppNotificationListener from "./components/InAppNotificationListener";
import ProSetupFlowModal from "./components/ProSetupFlowModal";
import SentryUserSync from "./components/SentryUserSync";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useHideKeyboardAccessory } from "@/hooks/useHideKeyboardAccessory";

const queryClient = new QueryClient();

const OnlineGate = ({ children }: { children: React.ReactNode }) => {
  const isOnline = useOnlineStatus();
  if (!isOnline) return <OfflineScreen />;
  return <>{children}</>;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/signin" replace />;
  return <>{children}</>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  if (typeof window !== "undefined" && localStorage.getItem("kin_splash_seen") !== "true") {
    return <Navigate to="/welcome" replace />;
  }
  return <>{children}</>;
};

const SplashRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  if (typeof window !== "undefined" && localStorage.getItem("kin_splash_seen") === "true") {
    return <Navigate to="/signin" replace />;
  }
  return <SplashLanding />;
};

/** Issue #331 — turns off the iOS form accessory bar ("Done" + ‹ › arrows)
 *  once, for the whole webview. Mounted above OnlineGate so it fires even when
 *  the app opens offline or on the sign-in screen, and rendered as a host
 *  component because App itself is an expression-bodied component with no
 *  hooks. Renders nothing; touches no keyboard measurement state. */
const KeyboardAccessoryHost = () => {
  useHideKeyboardAccessory();
  return null;
};

const ReviewPromptHost = () => {
  const { showReviewPrompt, dismissReviewPrompt } = useAuth();
  if (!showReviewPrompt) return null;
  return <ReviewPrompt onDismiss={dismissReviewPrompt} />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        {/* #1529 — mirrors the route onto Sentry's scope. Above OnlineGate on
            purpose: that gate unmounts its subtree when the network drops, and
            an offline error is exactly the one whose route we must not lose.
            See @/lib/sentryRoute for the SDK guard this works around. */}
        <SentryRouteSync />
        <KeyboardAccessoryHost />
        <OnlineGate>
        <AuthProvider>
          {/* #642 — mirrors the user id onto Sentry's scope. Mounted as the
              first child of AuthProvider, above the routes, so an error on the
              sign-in or callback screens is attributed too. Renders nothing. */}
          <SentryUserSync />
          <UserPreferencesProvider>
          <GoogleCalendarProvider>
          <AppleCalendarProvider>
          <KrogerProvider>
            <Routes>
              <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/pro" element={<ProtectedRoute><Pro /></ProtectedRoute>} />
              <Route path="/welcome" element={<SplashRoute />} />
              <Route path="/signin" element={<PublicRoute><SignIn /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/kroger/callback" element={<KrogerCallback />} />
              <Route path="/pinterest/callback" element={<PinterestCallback />} />
              <Route path="/google/callback" element={<GoogleCallback />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <AppStoreNudge />
            <ReviewPromptHost />
            <EntitlementFlipListener />
            {/* #803 — presence heartbeat + the in-app half of "toast instead of
                a text when she's still here". Renders nothing. */}
            <InAppNotificationListener />
            <ProSetupFlowModal />
          </KrogerProvider>
          </AppleCalendarProvider>
          </GoogleCalendarProvider>
          </UserPreferencesProvider>
        </AuthProvider>
        </OnlineGate>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
